'use client'

import { Component, type ErrorInfo, type ReactNode } from 'react'
import { reportRumError } from '~/src/lib/reportRumError'

interface RumErrorBoundaryProps {
  fallback: () => ReactNode
  children: ReactNode
}

interface RumErrorBoundaryState {
  hasError: boolean
}

/**
 * Drop-in replacement for `@datadog/browser-rum-react`'s ErrorBoundary that
 * doesn't statically import the RUM SDK — the DD one drags ~65 KB gz into
 * every page's critical bundle even when sampling is 0. Reporting goes
 * through a lazy import and no-ops unless instrumentation-client.ts actually
 * initialized the SDK.
 */
export class RumErrorBoundary extends Component<
  RumErrorBoundaryProps,
  RumErrorBoundaryState
> {
  override state: RumErrorBoundaryState = { hasError: false }

  static getDerivedStateFromError(): RumErrorBoundaryState {
    return { hasError: true }
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error(error)
    reportRumError(error, {
      react: { componentStack: errorInfo.componentStack },
    })
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      return this.props.fallback()
    }
    return this.props.children
  }
}
